import type { ComputedRef } from 'vue';

import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAbility } from '@casl/vue';
import { jwtDecode } from 'jwt-decode';
import _cloneDeep from 'lodash/cloneDeep';

import { updateAbility } from '@/helpers/casl';
import { AuthData } from '@/models/auth';

const defAuthData: AuthData = {
  refresh: '',
  access: '',
  permissions: [],
  exp: 0,
};

const localStorageKeyName = 'auth_data';

function getLocalStorageAuthData(): AuthData {
  const data: string | null = localStorage.getItem(localStorageKeyName);
  let response: AuthData;
  if (data) {
    response = JSON.parse(data);
  } else {
    response = _cloneDeep(defAuthData);
  }
  return response;
}

const usePrivateAuthStore = defineStore('_auth', () => {
  const authData = ref(getLocalStorageAuthData());
  function $reset(): void {
    authData.value = getLocalStorageAuthData();
  }
  return { authData, $reset };
});

export const useAuthStore = defineStore('auth', () => {
  const _authStore = usePrivateAuthStore();
  const ability = useAbility();

  const isStaff = ref(false);

  function $reset(): void {
    _authStore.$reset();
    isStaff.value = false;
  }

  watch(
    () => _authStore.authData,
    function (newValues, oldValues) {
      updateAbility(ability, _authStore.authData);
      isStaff.value = ability.can('staff', 'Job');
    },
    { immediate: true },
  );

  const isLoggedIn = computed(() => !!_authStore.authData.access);
  const authData = computed(() => _authStore.authData);
  const getAccessTokenExpTime = computed(() => {
    const token = _authStore.authData.access;
    if (!token) return 0;
    const accessToken: AuthData = jwtDecode(token);
    return accessToken.exp;
  });
  const getRefreshTokenExpTime = computed(() => {
    const token = _authStore.authData.refresh;
    if (!token) return 0;
    const refreshToken: AuthData = jwtDecode(token);
    return refreshToken.exp;
  });
  const isRefreshTokenValid = (now: number): ComputedRef<boolean> =>
    computed(() => {
      const exp = getRefreshTokenExpTime.value;
      return exp > now;
    });
  const isAccessTokenValid = (now: number): ComputedRef<boolean> =>
    computed(() => {
      const exp = getAccessTokenExpTime.value;
      return exp > now;
    });

  function addAuthData(authData: AuthData): void {
    _authStore.authData = authData;
    localStorage.setItem(localStorageKeyName, JSON.stringify(authData));
  }

  function removeAuthData(): void {
    addAuthData(_cloneDeep(defAuthData));
  }

  function updateAccessToken(access: string): void {
    _authStore.authData.access = access;
    addAuthData(_authStore.authData);
  }

  return {
    $reset,
    isStaff,
    isLoggedIn,
    authData,
    getAccessTokenExpTime,
    getRefreshTokenExpTime,
    isRefreshTokenValid,
    isAccessTokenValid,
    addAuthData,
    removeAuthData,
    updateAccessToken,
  };
});
